import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';

import {
  HeaderRow,
  TitleContainer,
  SubTitle,
  Title,
} from '../../../components/v2/Header';
import ConfirmationModal from '../../../components/v2/ConfirmationModal';
import FirebaseContext from '../../../context/FirebaseContext';
import LoggedInUserContext from '../../../context/LoggedInUserContext';
import { withCoachesListContextReady } from '../../context/CoachesListContext';
import LoadingOverlay from '../../components/LoadingOverlay';
import useComponentMounted from '../../../hooks/useComponentMounted';
import useToast from '../../hooks/useToast';
import useRunWatchdog from '../../hooks/useRunWatchdog';
import MigrationRun, { MigrationRunStatus } from '../../Model/MigrationRun';
import {
  listMigratableWorkouts,
  runMigration,
  summarizeMigrationRun,
  summarizeMigrationRunDocument,
} from '../../utils/dataMigration';

import {
  MigrationRunOutcome,
  captureMigrationToolOpened,
  captureMigrationCoachPairSelected,
  captureMigrationWorkoutListLoaded,
  captureMigrationBlockedWorkoutsViewed,
  captureMigrationStarted,
  captureMigrationWorkoutSucceeded,
  captureMigrationWorkoutFailed,
  captureMigrationCompleted,
} from '../../utils/migrationTelemetry';

import CoachPairSelector from './components/CoachPairSelector';
import MigratableWorkoutsTable from './components/MigratableWorkoutsTable';
import MigrationRunBar from './components/MigrationRunBar';
import MigrationResultModal from './components/MigrationResultModal';
import { Container } from './styles';
import texts from './texts.json';

const DataMigration = () => {
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { userId: acId } = useContext(LoggedInUserContext);
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  const [sourceCoach, setSourceCoach] = useState(null);
  const [targetCoach, setTargetCoach] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [migratableCount, setMigratableCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isConfirmingRun, setIsConfirmingRun] = useState(false);
  const [run, setRun] = useState(null);
  const [runTotal, setRunTotal] = useState(0);
  const [runSummary, setRunSummary] = useState(null);
  /*
    Set only when the request died on a run the endpoint may still be finishing, which is
    the one case where the document is the whole account of the run. A run that answers
    settles on its response, which carries per-workout detail the document does not keep.
  */
  const [isFollowingRun, setIsFollowingRun] = useState(false);

  const sourceCoachId = sourceCoach?.id;
  const targetCoachId = targetCoach?.id;
  const isRunning = !!run;
  /*
    Progress comes off the run document the endpoint is writing, not from this page — the
    run belongs to the server, and the tab only holds a view of it. Reading it through the
    observed document is what keeps the count live under one request.
  */
  const runProgress = run && { migrated: run.migrated, total: run.total || runTotal };
  /*
    Read here rather than in the effect below: `observer` tracks what the render reads, so
    a status the effect only reads inside its body would never wake it.
  */
  const isRunFinished = !!run?.isFinished;
  const runDeadlineAt = run?.deadlineAt?.getTime();
  /*
    The list endpoint is slow enough on a large library that a second source coach can be
    picked while the first is still in flight. Every request takes a token and only the
    newest one is allowed to commit, so a slower earlier response cannot overwrite the
    coach now on screen, raise its error toast, or clear the overlay under a live request.
  */
  const libraryRequestRef = useRef(0);
  // Guards re-entry into the run within a single tick, which state cannot do.
  const isRunningRef = useRef(false);
  /*
    Held while a run is being followed, so a run that turns out never to have been opened can
    hand the selection back rather than making the operator pick it again.
  */
  const followedSelectionRef = useRef([]);
  // Opening the tool is one event per visit, not one per render.
  const hasCapturedToolOpenedRef = useRef(false);
  /*
    When the run started, for duration_ms. On a ref because the run can end on either the
    response or the document, and neither path should re-render to carry a start time.
  */
  const runStartedAtRef = useRef(0);

  const loadLibrary = useCallback(async () => {
    libraryRequestRef.current += 1;
    const requestId = libraryRequestRef.current;
    const isLatestRequest = () => isComponentMountedRef.current && libraryRequestRef.current === requestId;

    setIsLoading(true);
    const startedAt = Date.now();
    try {
      const result = await listMigratableWorkouts(sourceCoachId, remote);
      if (isLatestRequest()) {
        setWorkouts(result.workouts);
        setMigratableCount(result.migratableCount);

        /*
          Only the request that commits reports. A superseded response is a library the
          operator never saw, and counting it would attribute another coach's numbers to
          the one now on screen.
        */
        captureMigrationWorkoutListLoaded(acId, {
          sourceCoachId,
          totalCount: result.totalCount,
          migratableCount: result.migratableCount,
          blockedCount: result.blockedCount,
          loadTimeMs: Date.now() - startedAt,
        });

        if (result.blockedCount > 0) {
          captureMigrationBlockedWorkoutsViewed(acId, {
            sourceCoachId,
            blockedCount: result.blockedCount,
            workouts: result.workouts,
          });
        }
      }
    } catch (error) {
      if (isLatestRequest()) {
        setWorkouts([]);
        setMigratableCount(0);
        showToast(error.message || texts.loadError, { error: true });
      }
    }
    if (isLatestRequest()) {
      setIsLoading(false);
    }
  }, [
    acId,
    sourceCoachId,
    remote,
    showToast,
    isComponentMountedRef,
  ]);

  useEffect(() => {
    if (hasCapturedToolOpenedRef.current) {
      return;
    }
    hasCapturedToolOpenedRef.current = true;
    captureMigrationToolOpened(acId);
  }, [acId]);

  /*
    The pair, not either half: re-picking a target against the same source is a new pair and
    reports again, which is what "source and target both selected" means for an operator who
    is trying targets.
  */
  useEffect(() => {
    if (!sourceCoachId || !targetCoachId) {
      return;
    }
    captureMigrationCoachPairSelected(acId, { sourceCoachId, targetCoachId });
  }, [acId, sourceCoachId, targetCoachId]);

  useEffect(() => {
    setSelectedIds([]);

    if (!sourceCoachId) {
      // Disowns whatever is in flight, so a pending response cannot land on the cleared page.
      libraryRequestRef.current += 1;
      setWorkouts([]);
      setMigratableCount(0);
      setIsLoading(false);
      return;
    }

    loadLibrary();
  }, [
    sourceCoachId,
    loadLibrary,
  ]);

  const onRunConfirmed = useCallback(async () => {
    /*
      A second click on Yes lands here again: MUI keeps the dialog clickable through its
      exit fade, and the selection is only cleared once the run is done. Without this the
      same selection would be sent twice and land duplicate copies.
    */
    if (isRunningRef.current) {
      return;
    }
    isRunningRef.current = true;
    setIsConfirmingRun(false);

    captureMigrationStarted(acId, {
      sourceCoachId,
      targetCoachId,
      workoutCount: selectedIds.length,
    });
    runStartedAtRef.current = Date.now();

    // Reserved before the request so the document can be watched while it is in flight.
    const runId = MigrationRun.reserveRunId();
    // The selection count stands in for the run's total until the document exists.
    setRunTotal(selectedIds.length);
    setRun(new MigrationRun(runId));

    try {
      const result = await runMigration({
        sourceCoachId,
        targetCoachId,
        workoutIds: selectedIds,
        runId,
        remote,
      });

      if (!isComponentMountedRef.current) {
        return;
      }

      /*
        Reported off the raw results rather than the summary, which drops newWorkoutId and
        keeps only what the result modal renders.
      */
      result.results.forEach(({
        sourceWorkoutId,
        success,
        newWorkoutId,
        suffixApplied,
        failureReason,
      }) => {
        if (success) {
          captureMigrationWorkoutSucceeded(acId, {
            sourceWorkoutId,
            newWorkoutId,
            targetCoachId,
            suffixApplied: !!suffixApplied,
          });
        } else {
          captureMigrationWorkoutFailed(acId, { sourceWorkoutId, targetCoachId, failureReason });
        }
      });

      captureMigrationCompleted(acId, {
        workoutCount: result.results.length,
        successCount: result.migratedCount,
        failureCount: result.failedCount,
        durationMs: Date.now() - runStartedAtRef.current,
        outcome: MigrationRunOutcome.COMPLETED,
        isSuccessDetailReported: true,
      });

      setRunSummary(summarizeMigrationRun(result, workouts));
      setSelectedIds([]);

      if (result.failedCount > 0) {
        showToast(format(texts.runFailures, { count: result.failedCount }), { error: true });
      } else {
        showToast(format(texts.runSuccess, { count: result.migratedCount }));
      }

      // The response answered for every workout in the run, so the document has nothing to add.
      setRun(null);
      isRunningRef.current = false;
    } catch (error) {
      if (!isComponentMountedRef.current) {
        return;
      }

      /*
        A rejected run never started, so there is no document to wait on and the selection
        is still worth keeping for a retry.
      */
      if (error.isRunRejected) {
        captureMigrationCompleted(acId, {
          workoutCount: selectedIds.length,
          successCount: 0,
          failureCount: 0,
          durationMs: Date.now() - runStartedAtRef.current,
          outcome: MigrationRunOutcome.REJECTED,
          isSuccessDetailReported: true,
          rejectionReason: error.message,
          statusCode: error.status,
        });

        showToast(error.message || texts.runRejected, { error: true });
        setRun(null);
        isRunningRef.current = false;
        return;
      }

      /*
        The request died, which does not mean the run did: the endpoint owns it and may
        still be finishing. Keep observing the document — the only account of the run left —
        and report what it ends up saying rather than what the request did.
      */
      followedSelectionRef.current = selectedIds;
      setSelectedIds([]);
      setIsFollowingRun(true);
    }
  }, [
    acId,
    sourceCoachId,
    targetCoachId,
    selectedIds,
    workouts,
    remote,
    showToast,
    isComponentMountedRef,
  ]);

  /*
    A run the platform kills never writes a terminal status, and a request that died before the
    endpoint opened the document leaves nothing to wait on at all — either way the effect below
    would wait forever, selectors locked. The watchdog calls both: stalled once `deadlineAt` (the
    endpoint's own timeout) passes, missing when no document has appeared after a grace period.
  */
  const {
    // A followed run that passed its deadline without closing: killed, not still working.
    isTimedOut: isRunStalled,
    // A followed run whose document never appeared: it was never opened, so nothing was copied.
    isMissing: isRunMissing,
  } = useRunWatchdog({
    isWatching: isFollowingRun,
    isFinished: isRunFinished,
    // The deadline is written with the document, so it stands in for the document existing.
    hasDoc: !!runDeadlineAt,
    deadlineMs: runDeadlineAt,
  });

  /*
    Settles a run whose request died, off the document rather than off the request. Only
    that path reaches here — a run that answered has already cleared itself above.
  */
  useEffect(() => {
    if (!isFollowingRun || (!isRunFinished && !isRunStalled && !isRunMissing)) {
      return;
    }

    if (isRunMissing) {
      /*
        The run was never opened, so nothing was copied and there is nothing to check. Same
        answer as a run the endpoint turned away outright, including handing the selection
        back — the operator's next move is to run it again, not to rebuild the selection.
      */
      captureMigrationCompleted(acId, {
        workoutCount: followedSelectionRef.current.length,
        successCount: 0,
        failureCount: 0,
        durationMs: Date.now() - runStartedAtRef.current,
        outcome: MigrationRunOutcome.NEVER_STARTED,
        isSuccessDetailReported: true,
      });

      showToast(texts.runNeverStarted, { error: true });
      setSelectedIds(followedSelectionRef.current);
    } else if (isRunFinished) {
      const summary = summarizeMigrationRunDocument(run, workouts);

      /*
        The document names failures but only counts successes, so this path reports the
        failures individually and leaves migration_completed to account for the rest —
        success_detail_reported marks it so the two shapes are not summed as though alike.
      */
      summary.failed.forEach(({ id, reason }) => captureMigrationWorkoutFailed(acId, {
        sourceWorkoutId: id,
        targetCoachId,
        failureReason: reason,
      }));

      captureMigrationCompleted(acId, {
        workoutCount: run.total || runTotal,
        successCount: run.migrated,
        failureCount: run.failed,
        durationMs: Date.now() - runStartedAtRef.current,
        outcome: MigrationRunOutcome.COMPLETED,
        isSuccessDetailReported: false,
      });
      // A run that ended before copying anything has nothing to show but its toast.
      if (summary.migratedCount > 0 || summary.failed.length > 0) {
        setRunSummary(summary);
      }

      if (run.status === MigrationRunStatus.FAILED) {
        showToast(run.error || texts.runFailed, { error: true });
      } else if (run.failed > 0) {
        showToast(format(texts.runFailures, { count: run.failed }), { error: true });
      } else {
        showToast(format(texts.runSuccess, { count: run.migrated }));
      }
    } else {
      /*
        Past its deadline and still open, so the run was killed before it could record how
        it ended. The counts are the last ones written and no summary can honestly call
        itself the outcome — the library itself is what the operator has to check. Those last
        counts are still reported as the stalled run's completion: they are the only record
        it will ever have.
      */
      captureMigrationCompleted(acId, {
        workoutCount: run.total || runTotal,
        successCount: run.migrated,
        failureCount: run.failed,
        durationMs: Date.now() - runStartedAtRef.current,
        outcome: MigrationRunOutcome.STALLED,
        isSuccessDetailReported: false,
      });

      showToast(format(texts.runStalled, { count: run.migrated }), { error: true });
      loadLibrary();
    }

    // Not following any more, which also stands the watchdog down and clears its flags.
    setIsFollowingRun(false);
    setRun(null);
    isRunningRef.current = false;
  }, [
    acId,
    targetCoachId,
    runTotal,
    isFollowingRun,
    isRunFinished,
    isRunStalled,
    isRunMissing,
    run,
    workouts,
    showToast,
    loadLibrary,
  ]);

  const onSourceChange = useCallback((coach) => setSourceCoach(coach), []);
  const onTargetChange = useCallback((coach) => setTargetCoach(coach), []);
  const onRunRequested = useCallback(() => setIsConfirmingRun(true), []);
  const onRunCancelled = useCallback(() => setIsConfirmingRun(false), []);
  /*
    The refresh waits for the summary to be dismissed: LoadingOverlay portals into #root at
    z-index 9900 and the modal is a MUI dialog at 1300, so refreshing underneath it would
    bury the results behind a spinner. Nothing can be re-run before this point anyway —
    the modal blocks the page — so eligibility is still re-evaluated before a second run.
  */
  const onResultDismissed = useCallback(() => {
    setRunSummary(null);
    loadLibrary();
  }, [loadLibrary]);

  return (
    <Container>
      <HeaderRow>
        <TitleContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.subTitle}</SubTitle>
        </TitleContainer>
      </HeaderRow>
      <CoachPairSelector
        sourceCoach={sourceCoach}
        targetCoach={targetCoach}
        onSourceChange={onSourceChange}
        onTargetChange={onTargetChange}
        /*
          The run captured its target when it started, so letting the pair change mid-run
          would leave the page naming a coach the workouts did not go to.
        */
        disabled={isRunning}
      />
      <MigrationRunBar
        selectedCount={selectedIds.length}
        hasTargetCoach={!!targetCoachId}
        isSameCoach={!!sourceCoachId && sourceCoachId === targetCoachId}
        isRunning={isRunning}
        progress={runProgress}
        onRun={onRunRequested}
      />
      <MigratableWorkoutsTable
        workouts={workouts}
        hasSourceCoach={!!sourceCoachId}
        migratableCount={migratableCount}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        isRunning={isRunning}
      />
      <ConfirmationModal
        open={isConfirmingRun}
        title={texts.confirmTitle}
        description={format(texts.confirmDescription, {
          count: selectedIds.length,
          source: sourceCoach?.label,
          target: targetCoach?.label,
        })}
        onClose={onRunCancelled}
        onConfirm={onRunConfirmed}
      />
      {!!runSummary && (
        <MigrationResultModal
          summary={runSummary}
          onClose={onResultDismissed}
        />
      )}
      <LoadingOverlay isLoading={isLoading} />
    </Container>
  );
};

/*
  No provider here: every /a/:userId page renders under ToolsLayout, which is already
  composed with withCoachesListContextProvider. Adding a second one mounts a second
  provider that re-runs the coach and assignment queries on every visit and shadows the
  context the layout has already loaded.
*/
export default compose(
  withCoachesListContextReady,
  observer,
)(DataMigration);
